import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { u, IMG } from "@/lib/site";

const SECTIONS = [
    {
        id: "softwood",
        num: "01",
        name: "SOFTWOOD",
        species: ["Pine", "SYP", "Spruce"],
        images: [
            { id: IMG.pine, alt: "Sawn pine and SYP softwood planks stacked in a timber yard" },
            { id: IMG.spruce, alt: "Spruce softwood timber planks" },
        ],
        forms: [
            "Round Logs",
            "Sawn Sizes",
            "Imported KD (Kiln-Dried) Planks",
            "Locally Heat-Treated & Planed Timber",
            "Customized Sizes",
        ],
        note: "Suitable for packaging, industrial and decorative applications.",
        theme: "light",
    },
    {
        id: "hardwood",
        num: "02",
        name: "HARDWOOD",
        species: ["Meranti", "Malaysian Sal", "Wana"],
        images: [
            { id: IMG.grainDark, alt: "Meranti hardwood timber grain close-up" },
            { id: IMG.grainDark2, alt: "Malaysian Sal and Wana hardwood timber grain" },
        ],
        forms: ["Round Logs", "Sawn Sizes"],
        note: "Selected to meet diverse commercial and application-specific requirements.",
        theme: "warm",
    },
    {
        id: "teak",
        num: "03",
        name: "TEAK",
        species: ["Round Logs", "Rough Squares", "Sawn Sizes"],
        images: [
            { id: IMG.teakLogs, alt: "Teak round logs ready for processing" },
            { id: IMG.planks, alt: "Precision sawn teak planks in stacks" },
        ],
        forms: ["Round Logs", "Rough Squares", "Sawn Sizes"],
        note: "Currently supplied in multiple forms, with customized teak processing coming soon.",
        theme: "dark",
    },
];

const THEMES = {
    light: {
        section: "bg-cream",
        heading: "text-ink",
        body: "text-mutedw",
        chip: "border-line text-ink/80",
        formText: "text-ink/85",
        marker: "bg-teak",
        num: "text-ink/10",
    },
    warm: {
        section: "bg-sand",
        heading: "text-ink",
        body: "text-mutedw",
        chip: "border-ink/15 text-ink/80",
        formText: "text-ink/85",
        marker: "bg-teak",
        num: "text-ink/10",
    },
    dark: {
        section: "bg-ink",
        heading: "text-cream",
        body: "text-cream/65",
        chip: "border-linedark text-cream/80",
        formText: "text-cream/85",
        marker: "bg-gold",
        num: "text-cream/10",
    },
};

export default function Products() {
    return (
        <>
            <Seo
                title="Our Timber Range | ISHAAN WORLDLINK LLP"
                description="Teak, Softwood (Pine, SYP, Spruce) and Hardwood (Meranti, Malaysian Sal, Wana) — round logs, sawn sizes, KD planks and customized sizes from Gandhidham."
            />

            {/* HERO */}
            <section className="relative flex min-h-[62svh] items-end overflow-hidden bg-ink" data-testid="products-hero">
                <img
                    src={u(IMG.planks, 2000)}
                    srcSet={`${u(IMG.planks, 900)} 900w, ${u(IMG.planks, 1600)} 1600w, ${u(IMG.planks, 2200)} 2200w`}
                    sizes="100vw"
                    alt="Precision sawn timber planks stacked in a kiln-drying yard"
                    className="absolute inset-0 h-full w-full object-cover"
                    fetchPriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
                <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-44 sm:px-6 sm:pb-20 lg:px-8">
                    <Reveal y={16}>
                        <Eyebrow dark>Products</Eyebrow>
                        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.02] tracking-tight text-cream sm:text-5xl lg:text-6xl">
                            OUR TIMBER RANGE
                        </h1>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg">
                            Teak, Softwood and Hardwood — supplied in the forms our customers
                            need, with customized teak processing on the horizon.
                        </p>
                    </Reveal>
                </div>
            </section>

            {SECTIONS.map((s) => {
                const t = THEMES[s.theme];
                return (
                    <section
                        key={s.id}
                        className={`py-20 sm:py-28 ${t.section}`}
                        data-testid={`product-section-${s.id}`}
                    >
                        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
                            <div className="lg:col-span-5">
                                <Reveal>
                                    <p className={`font-display text-6xl font-semibold leading-none sm:text-7xl ${t.num}`}>
                                        {s.num}
                                    </p>
                                    <h2 className={`mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl ${t.heading}`}>
                                        {s.name}
                                    </h2>
                                    <div className="mt-5 flex flex-wrap gap-2">
                                        {s.species.map((sp) => (
                                            <span
                                                key={sp}
                                                className={`border px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.14em] ${t.chip}`}
                                            >
                                                {sp}
                                            </span>
                                        ))}
                                    </div>
                                    <p className={`mt-7 text-xs font-semibold uppercase tracking-[0.25em] ${s.theme === "dark" ? "text-gold" : "text-teak"}`}>
                                        Available in
                                    </p>
                                    <ul className="mt-4 space-y-3">
                                        {s.forms.map((f) => (
                                            <li key={f} className={`flex items-center gap-3 text-sm ${t.formText}`}>
                                                <span className={`h-1.5 w-1.5 shrink-0 ${t.marker}`} />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className={`mt-7 max-w-md text-sm leading-relaxed ${t.body}`}>{s.note}</p>
                                    {s.id === "teak" && (
                                        <p className="mt-8 font-display text-2xl font-semibold tracking-tight text-gold sm:text-3xl" data-testid="teak-specify-line">
                                            YOU SPECIFY. WE PROCESS.
                                        </p>
                                    )}
                                </Reveal>
                            </div>
                            <div className="lg:col-span-7">
                                <div className="grid grid-cols-2 gap-4">
                                    {s.images.map((im, i) => (
                                        <Reveal key={im.id} delay={0.08 * i} className={i === 1 ? "sm:mt-10" : ""}>
                                            <div className="group overflow-hidden">
                                                <img
                                                    src={u(im.id, 900)}
                                                    alt={im.alt}
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="aspect-[3/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                                />
                                            </div>
                                        </Reveal>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                );
            })}

            <Marquee items={["PINE", "SYP", "SPRUCE", "MERANTI", "MALAYSIAN SAL", "WANA", "TEAK"]} />

            {/* CTA */}
            <section className="bg-cream py-20 sm:py-24" data-testid="products-cta">
                <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <h2 className="max-w-xl font-display text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl">
                            Tell us the size. We will cut to it.
                        </h2>
                        <p className="mt-3 max-w-lg text-base leading-relaxed text-mutedw">
                            Length. Width. Thickness. Quantity. All Tailored to Your Requirement.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Link
                            to="/contact"
                            data-testid="products-enquire-btn"
                            className="group inline-flex items-center gap-2 bg-ink px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-teak"
                        >
                            Enquire Now
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
